// Small utility to set per-page SEO meta tags from React components.
// Handles: title, description, canonical, Open Graph, Twitter Card, hreflang.
// Kept dependency-free — no react-helmet — so it works fine with our CSR setup.

const PROD_ORIGIN = "https://retablirmoncredit.com";

function upsertMeta(selector, attrName, attrValue, contentKey, contentValue) {
  if (!contentValue) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute(contentKey, contentValue);
}

function upsertLink(rel, hreflang, href) {
  if (!href) return;
  const sel = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Set all SEO tags in one call from a page component's useEffect.
 * @param {Object} opts
 * @param {string} opts.title - Full <title> tag content.
 * @param {string} opts.description - meta description (155-160 chars ideal).
 * @param {string} [opts.canonicalPath] - Path portion, e.g. "/approval/situations/faillite".
 * @param {string} [opts.image] - Absolute URL to a 1200x630 social image.
 * @param {"website"|"article"} [opts.ogType]
 * @param {"fr"|"en"} [opts.lang] - Current UI language (drives og:locale + <html lang>).
 * @param {boolean} [opts.noindex] - If true, set robots noindex.
 */
export function setSeo(opts) {
  const {
    title,
    description,
    canonicalPath,
    image,
    ogType = "website",
    lang = "fr",
    noindex = false,
  } = opts || {};

  if (title) document.title = title;
  if (lang) document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-CA";

  upsertMeta('meta[name="description"]', "name", "description", "content", description);
  upsertMeta('meta[name="robots"]', "name", "robots", "content",
    noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

  // Canonical + hreflang. We use the production domain in canonical so that
  // once DNS is wired to retablirmoncredit.com, search engines converge on it
  // instead of the preview URL.
  const canonicalHref = canonicalPath ? `${PROD_ORIGIN}${canonicalPath}` : null;
  if (canonicalHref) {
    upsertLink("canonical", null, canonicalHref);
    upsertLink("alternate", "fr-CA", canonicalHref);
    upsertLink("alternate", "en-CA", canonicalHref);
    upsertLink("alternate", "x-default", canonicalHref);
  }

  // Open Graph
  upsertMeta('meta[property="og:title"]', "property", "og:title", "content", title);
  upsertMeta('meta[property="og:description"]', "property", "og:description", "content", description);
  upsertMeta('meta[property="og:type"]', "property", "og:type", "content", ogType);
  upsertMeta('meta[property="og:locale"]', "property", "og:locale", "content", lang === "fr" ? "fr_CA" : "en_CA");
  if (canonicalHref) {
    upsertMeta('meta[property="og:url"]', "property", "og:url", "content", canonicalHref);
  }
  if (image) {
    upsertMeta('meta[property="og:image"]', "property", "og:image", "content", image);
  }

  // Twitter Card
  upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "content", "summary_large_image");
  upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", "content", title);
  upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", "content", description);
  if (image) {
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", "content", image);
  }
}

/** Insert a JSON-LD block once per page (removed on next call from same page). */
export function setJsonLd(id, obj) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

/**
 * Build + inject a FAQPage JSON-LD block from an array of {q, a} objects.
 * Google renders these as rich FAQ accordions in SERPs — typically 2-3x CTR.
 */
export function setFaqSchema(id, faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q || f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a || f.answer,
      },
    })),
  };
  setJsonLd(id, schema);
}

/**
 * Build + inject a BreadcrumbList JSON-LD block. `items` is an ordered array
 * of {name, path} — path is a same-origin absolute path (e.g. "/approval").
 * The final item is treated as the current page (Google understands this
 * from position alone).
 */
export function setBreadcrumbSchema(id, items) {
  if (!Array.isArray(items) || items.length === 0) return;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path ? `${PROD_ORIGIN}${it.path}` : undefined,
    })),
  };
  setJsonLd(id, schema);
}
